import { NgInject } from '@df/ng/ng-inject';
import { Subject, Subscription } from 'rxjs';
import { uniqueId } from '@df/util/fn/unique-id';
import { TimeoutService } from '@df/ng/timeout.service';

export const STATUS_SUCCESS = 'SUCCESS';
export const STATUS_ERROR = 'ERROR';

export interface IStatusData {
    busy: boolean;
    superBusy: boolean; // its same as busy but will not be dropped with error/success setter
    error: boolean;
    success: boolean;
    message?: string;
}

export class Status {
    promise?: Promise<any>;
    data: IStatusData;
    lastHash?: string;
    errorData?: any;
    protected changed: boolean;
    protected timeout?: number;
    readonly subject: Subject<Status> = new Subject<Status>();

    @NgInject(TimeoutService)
    private timeoutService!: TimeoutService;

    constructor() {
        this.changed = false;
        this.data = {
            busy: false,
            superBusy: false,
            error: false,
            success: false
        };

        this['_oid'] = uniqueId();
        this.subject['_oid'] = uniqueId();
    }

    /**
     * Resets data
     */
    reset(): void {
        this.data.busy = false;
        this.data.error = false;
        this.errorData = undefined;
        this.data.success = false;
        this.data.message = undefined;
        this.next();
    }

    /**
     * Emits this to subscribers and sets changed flag
     */
    next(): void {
        clearTimeout(this.timeout);
        this.changed = true;
        this.subject.next(this);
        this.timeout = this.timeoutService.setTimeout(() => (this.changed = false), 3000);
    }

    /**
     * Registers observer
     * @returns subscription which allow to deregister observer
     */
    subscribe(...args): Subscription {
        return this.subject.subscribe(...args);
    }

    /**
     * Gets busy status
     */
    get busy(): boolean {
        return this.data.busy || this.data.superBusy;
    }

    /**
     * Sets busy status
     */
    set busy(value: boolean) {
        if (this.data.busy !== value) {
            this.data.busy = value;
            this.data.error = false;
            this.errorData = undefined;
            this.data.success = false;
            this.data.message = undefined;
            this.next();
        }
    }

    /**
     * Gets super busy status
     */
    get superBusy(): boolean {
        return this.data.superBusy;
    }

    /**
     * Sets super busy status
     */
    set superBusy(value: boolean) {
        this.data.superBusy = value;
        this.next();
    }

    /**
     * Returns true if status is busy or status was changed in last 3seconds
     */
    get important(): boolean {
        return this.busy || this.changed;
    }

    /**
     * Gets error message
     */
    get error(): string | undefined {
        if (this.data.error) {
            return this.data.message;
        }
        return undefined;
    }

    /**
     * Sets error status
     */
    set error(value: string | undefined | Error | { error: string }) {
        if (value instanceof Error) {
            value = value.message;
        } else if (typeof value === 'object' && value !== null) {
            value = value.error;
        } else if (typeof value !== 'string') {
            value = STATUS_ERROR;
        }
        this.data.message = value || STATUS_ERROR;
        this.data.error = true;
        this.errorData = undefined;
        this.data.busy = false;
        this.next();
    }

    /**
     * Gets success message
     */
    get success(): string | undefined {
        if (!this.data.error) {
            return this.data.message;
        }
        return undefined;
    }

    /**
     * Sets success status
     */
    set success(value: string | undefined) {
        this.data.message = value || STATUS_SUCCESS;
        this.data.error = false;
        this.errorData = undefined;
        this.data.busy = false;
        this.next();
    }

    /**
     * Gets message
     */
    get result(): string | undefined {
        return this.data.message;
    }
}
